import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { isSameDay } from "../lib/appointments";
import { buildWhatsAppLink, interpolateTemplate } from "../lib/marketing";
import { buildSystemAlerts } from "../lib/alerts";
import { useWhatsAppStatus } from "../hooks/useWhatsAppStatus";
import { setSetupReturnHome, clearReturnHome, openAlertTarget } from "../lib/setupRoute";
import { useSubscriptionsSummary } from "../hooks/useSubscriptionsSummary";
import { useWaitlistSummary } from "../hooks/useWaitlistSummary";
import { useAiUsageSummary } from "../hooks/useAiUsageSummary";

/**
 * Home — Landing page após login.
 * Bento Grid com 6 atalhos principais + KPIs ao vivo no topo.
 * Estética Vintage Club (dark + accent âmbar).
 */
const CARDS = [
    {
        id: "clients",
        title: "Central de Clientes",
        subtitle: "Ficha, gastos e próximos agendamentos",
        icon: "fa-address-book",
        span: "md:col-span-1 lg:col-span-2",
        accent: "from-amber-500/30 to-amber-400/5",
    },
    {
        id: "agenda",
        title: "Agenda",
        subtitle: "Hub central · timeline em tempo real",
        icon: "fa-calendar-days",
        span: "md:col-span-2 lg:col-span-2 lg:row-span-2",
        accent: "from-amber-400/35 to-orange-500/10",
        featured: true,
    },
    {
        id: "pos",
        title: "Terminal Balcão",
        subtitle: "Comandas e atendimento rápido",
        icon: "fa-cash-register",
        span: "md:col-span-1 lg:col-span-2",
        accent: "from-amber-500/30 to-amber-300/5",
    },
    {
        id: "admin",
        title: "Financeiro",
        subtitle: "KPIs, faturamento e comissões",
        icon: "fa-chart-line",
        span: "md:col-span-1 lg:col-span-2",
        accent: "from-emerald-500/25 to-amber-500/5",
    },
    {
        id: "catalog",
        title: "Catálogo & Estoque",
        subtitle: "Serviços, produtos e preços",
        icon: "fa-boxes-stacked",
        span: "md:col-span-1 lg:col-span-2",
        accent: "from-amber-500/25 to-amber-400/5",
    },
    {
        id: "employee",
        title: "Colaboradores",
        subtitle: "Gerencie sua equipe",
        icon: "fa-users",
        span: "md:col-span-1 lg:col-span-2",
        accent: "from-sky-500/25 to-amber-500/5",
    },
    {
        id: "whatsapp",
        title: "WhatsApp",
        subtitle: "Mensagens e agente IA",
        icon: "fa-whatsapp",
        iconStyle: "brand",
        span: "md:col-span-1 lg:col-span-2",
        accent: "from-emerald-500/25 to-emerald-400/5",
    },
    {
        id: "subscriptions",
        title: "Clube de Assinatura",
        subtitle: "Planos e assinantes",
        icon: "fa-crown",
        span: "md:col-span-1 lg:col-span-2",
        accent: "from-violet-500/25 to-amber-500/5",
    },
    {
        id: "account",
        title: "Configurações",
        subtitle: "Dados cadastrais, PINs e plano",
        icon: "fa-sliders",
        span: "md:col-span-2 lg:col-span-2",
        accent: "from-zinc-500/25 to-amber-500/5",
    },
];

// Smart alerts builder lives in /lib/alerts.js — re-exported here for back-compat.
export { buildSystemAlerts as buildHomeAlerts } from "../lib/alerts";

const Home = () => {
    const { brand, appointments, clients, products, services, professionals, templates, logMessage, setActiveModule, updateBrand } = useSalon();
    const now = new Date();

    // Estar na Home significa que o fluxo do checklist terminou (ou foi abandonado):
    // limpa qualquer flag "voltar pra Home" que tenha sobrado sem ser consumida.
    useEffect(() => { clearReturnHome(); }, []);

    const stats = useMemo(() => {
        const today = appointments.filter((a) => isSameDay(a.date, now) && a.status !== "cancelado");
        const inProgress = today.filter((a) => a.status === "em-andamento").length;
        const done = today.filter((a) => a.status === "concluido").length;
        const revenue = today
            .filter((a) => a.status === "concluido")
            .reduce((s, a) => s + (a.servicePrice || 0), 0);
        return { todayCount: today.length, inProgress, done, revenue, clientsCount: clients.length };
    }, [appointments, clients, now]);

    // ─── Mural de Alertas Inteligentes ────────────────────────────────────
    const { status: waStatus } = useWhatsAppStatus(10000);
    const subsSummary = useSubscriptionsSummary(60000);
    const waitlistSummary = useWaitlistSummary(30000);
    const aiUsage = useAiUsageSummary(60000);
    const alerts = useMemo(
        () => buildSystemAlerts({ products, clients, appointments, whatsapp: waStatus, subscriptionsSummary: subsSummary, waitlistSummary, aiUsage }),
        [products, clients, appointments, waStatus, subsSummary, waitlistSummary, aiUsage]
    );
    // ──────────────────────────────────────────────────────────────────────

    const hour = now.getHours();
    const greeting =
        hour < 12 ? "Bom dia" : hour < 18 ? "Boa tarde" : "Boa noite";

    return (
        <div data-testid="home-module" className="space-y-6">
            {/* Welcome strip */}
            <header
                data-testid="home-welcome"
                className="relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/8 via-black/60 to-black/80 p-6 lg:p-8"
            >
                <div
                    className="absolute -top-24 -right-24 h-72 w-72 rounded-full blur-3xl opacity-40 pointer-events-none"
                    style={{ background: `radial-gradient(circle, ${brand.primaryColor || "#d4af37"}55 0%, transparent 70%)` }}
                ></div>
                <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.35em] text-amber-300/70 font-bold">
                            {greeting}
                        </div>
                        <h1 className="salon-heading text-3xl lg:text-5xl text-amber-100 mt-2 leading-tight">
                            Bem-vindo
                        </h1>
                        <p className="mt-2 text-sm text-amber-200/70 max-w-xl">
                            Tudo o que você precisa para começar o turno está a um toque de distância.
                        </p>
                    </div>
                    <div className="text-left lg:text-right">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/60 font-bold">
                            {now.toLocaleDateString("pt-BR", { weekday: "long" })}
                        </div>
                        <div className="font-mono text-amber-200 text-2xl mt-1">
                            {now.toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                        </div>
                    </div>
                </div>

                {/* KPIs ao vivo */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="home-kpis">
                    <KpiPill icon="fa-calendar-day" label="Hoje" value={stats.todayCount} suffix="agendamentos" />
                    <KpiPill icon="fa-hourglass-half" label="Em andamento" value={stats.inProgress} suffix="serviços" highlight={stats.inProgress > 0} />
                    <KpiPill icon="fa-sack-dollar" label="Faturado hoje" value={`R$ ${stats.revenue.toFixed(2)}`} suffix={`${stats.done} concluídos`} accent="emerald" />
                    <KpiPill icon="fa-users" label="Clientes" value={stats.clientsCount} suffix="cadastrados" />
                </div>
            </header>

            <SetupChecklist
                brand={brand}
                clients={clients}
                services={services}
                products={products}
                professionals={professionals}
                setActiveModule={setActiveModule}
                updateBrand={updateBrand}
            />

            {/* Bento Grid */}
            <div
                data-testid="home-bento"
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 auto-rows-[180px] gap-4"
            >
                {CARDS.map((card) => (
                    <BentoCard key={card.id} card={card} onClick={() => setActiveModule(card.id)} />
                ))}
            </div>

            {/* Mural de Alertas Inteligentes */}
            <section data-testid="home-alerts" className="space-y-3">
                <div className="flex items-end justify-between gap-3">
                    <div>
                        <h2 className="salon-heading text-xl text-amber-100">Mural de Alertas</h2>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-amber-300/60 mt-0.5">
                            Inteligência operacional · clique para agir
                        </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider text-amber-200/40 font-mono">
                        {alerts.length} {alerts.length === 1 ? "sinal" : "sinais"}
                    </span>
                </div>

                {alerts.length === 0 ? (
                    <div
                        data-testid="home-alerts-empty"
                        className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-6 text-center"
                    >
                        <i className="fa-solid fa-mug-hot text-3xl text-emerald-400/70 mb-2"></i>
                        <p className="text-emerald-200/80 text-sm">Tudo em ordem hoje.</p>
                        <p className="text-emerald-200/40 text-[11px] mt-0.5">Aproveite o cafezinho.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {alerts.map((a) => (
                            <AlertCard
                                key={a.id}
                                alert={a}
                                onClick={() => {
                                    if (a.href) { window.location.href = a.href; return; }
                                    openAlertTarget(a, setActiveModule);
                                }}
                                onWhatsApp={() => {
                                    const tpl = templates.find((t) => t.id === a.whatsappTemplateId);
                                    if (!tpl || !a.whatsappClient) return;
                                    const msg = interpolateTemplate(tpl, a.whatsappClient, a.whatsappContext, brand);
                                    const link = buildWhatsAppLink(a.whatsappClient.phone, msg);
                                    window.open(link, "_blank", "noopener");
                                    logMessage({
                                        clientId: a.whatsappClient.id,
                                        channel: "whatsapp",
                                        campaignId: a.id === "birthdays" ? "cmp-aniversariantes" : "cmp-reativacao-corte",
                                        status: "delivered",
                                    });
                                    toast.success(`WhatsApp aberto · ${a.whatsappClient.name.split(" ")[0]}`, {
                                        description: "Mensagem registrada no histórico de campanhas.",
                                    });
                                }}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Footer hint — menu lateral só existe em telas md+ (tablet/desktop) */}
            <p className="hidden md:block text-center text-[11px] text-amber-200/40 italic">
                Você também pode navegar pelo menu lateral. <i className="fa-solid fa-arrow-left mx-1"></i>
            </p>
        </div>
    );
};

// ─── Bento Card ───────────────────────────────────────────────────────────
const BentoCard = ({ card, onClick }) => (
    <button
        data-testid={`home-card-${card.id}`}
        onClick={onClick}
        className={`group relative ${card.span} rounded-2xl border border-amber-500/15 bg-black/50 p-5 text-left overflow-hidden transition-all duration-300 hover:border-amber-400/55 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(212,175,55,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60`}
    >
        {/* Background gradient sutil */}
        <div
            className={`absolute inset-0 bg-gradient-to-br ${card.accent} opacity-60 group-hover:opacity-100 transition-opacity duration-500`}
        ></div>
        {/* Grain overlay */}
        <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay"
            style={{
                backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' /></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
            }}
        ></div>

        <div className="relative h-full flex flex-col">
            {/* Icon */}
            <div className="flex items-start justify-between">
                <div
                    className={`relative h-14 w-14 rounded-2xl flex items-center justify-center border border-amber-400/30 bg-gradient-to-br from-amber-500/25 to-amber-700/5 text-amber-300 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 ${
                        card.featured ? "h-16 w-16" : ""
                    }`}
                >
                    <i className={`${card.iconStyle === "brand" ? "fa-brands" : "fa-solid"} ${card.icon} ${card.featured ? "text-2xl" : "text-xl"}`}></i>
                    <div className="absolute inset-0 rounded-2xl ring-1 ring-amber-400/0 group-hover:ring-amber-400/50 transition-all"></div>
                </div>
                <i className="fa-solid fa-arrow-up-right text-amber-300/40 group-hover:text-amber-200 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"></i>
            </div>

            {/* Title + subtitle */}
            <div className="mt-auto">
                <h3 className={`salon-heading text-amber-100 leading-tight ${card.featured ? "text-2xl lg:text-3xl" : "text-lg lg:text-xl"}`}>
                    {card.title}
                </h3>
                <p className="text-[11px] text-amber-200/55 mt-1 truncate">
                    {card.subtitle}
                </p>
            </div>
        </div>

        {/* Bottom hover accent */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
    </button>
);

// ─── Alert Card ───────────────────────────────────────────────────────────
const ALERT_PALETTE = {
    critical: {
        border: "border-red-500/40 hover:border-red-400/70",
        bg: "from-red-500/12 to-red-500/2",
        icon: "text-red-300",
        iconBg: "from-red-500/30 to-red-700/10 border-red-400/40",
        title: "text-red-100",
        detail: "text-red-200/70",
        cta: "text-red-200",
        kindLabel: "Crítico",
    },
    warning: {
        border: "border-amber-500/30 hover:border-amber-400/60",
        bg: "from-amber-500/10 to-amber-500/2",
        icon: "text-amber-300",
        iconBg: "from-amber-500/25 to-amber-700/5 border-amber-400/35",
        title: "text-amber-100",
        detail: "text-amber-200/70",
        cta: "text-amber-200",
        kindLabel: "Atenção",
    },
    info: {
        border: "border-sky-500/30 hover:border-sky-400/60",
        bg: "from-sky-500/10 to-sky-500/2",
        icon: "text-sky-300",
        iconBg: "from-sky-500/25 to-sky-700/5 border-sky-400/40",
        title: "text-sky-100",
        detail: "text-sky-200/70",
        cta: "text-sky-200",
        kindLabel: "Insight",
    },
    success: {
        border: "border-emerald-500/30 hover:border-emerald-400/60",
        bg: "from-emerald-500/10 to-emerald-500/2",
        icon: "text-emerald-300",
        iconBg: "from-emerald-500/25 to-emerald-700/5 border-emerald-400/40",
        title: "text-emerald-100",
        detail: "text-emerald-200/70",
        cta: "text-emerald-200",
        kindLabel: "Boa notícia",
    },
};

// ─── Confetti Burst (pure CSS, no deps) ──────────────────────────────────
const CONFETTI_COLORS = ["#d4af37", "#fbbf24", "#10b981", "#34d399", "#a855f7", "#ec4899", "#f97316", "#22d3ee"];
const ConfettiBurst = () => {
    const pieces = useMemo(() =>
        Array.from({ length: 80 }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 0.35,
            duration: 2.2 + Math.random() * 1.6,
            rotate: Math.random() * 360,
            size: 6 + Math.round(Math.random() * 8),
            color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            drift: -40 + Math.random() * 80,
        })), []);
    return (
        <div
            data-testid="confetti-burst"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-20 overflow-hidden"
        >
            <style>{`
                @keyframes confetti-fall {
                    0% { transform: translate3d(0, -10%, 0) rotate(0deg); opacity: 1; }
                    80% { opacity: 1; }
                    100% { transform: translate3d(var(--drift, 0px), 110%, 0) rotate(var(--rot, 720deg)); opacity: 0; }
                }
            `}</style>
            {pieces.map((p) => (
                <span
                    key={p.id}
                    className="absolute rounded-sm"
                    style={{
                        left: `${p.left}%`,
                        top: 0,
                        width: p.size,
                        height: p.size * 0.55,
                        background: p.color,
                        "--drift": `${p.drift}px`,
                        "--rot": `${p.rotate + 540}deg`,
                        animation: `confetti-fall ${p.duration}s ${p.delay}s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
                        boxShadow: `0 0 6px ${p.color}66`,
                    }}
                />
            ))}
        </div>
    );
};

// ─── Setup Checklist ─────────────────────────────────────────────────────
// Mesmo objeto default de horários do OnboardingWizard — usado só pra
// detectar se o tenant já customizou os horários ou ainda está no padrão
// sugerido (item 6 do checklist considera "padrão intocado" como pendente).
const DEFAULT_SALON_HOURS_JSON = JSON.stringify({
    mon: { open: true, from: "09:00", to: "20:00" },
    tue: { open: true, from: "09:00", to: "20:00" },
    wed: { open: true, from: "09:00", to: "20:00" },
    thu: { open: true, from: "09:00", to: "20:00" },
    fri: { open: true, from: "09:00", to: "21:00" },
    sat: { open: true, from: "09:00", to: "18:00" },
    sun: { open: false, from: "10:00", to: "16:00" },
});
const hasCustomSalonHours = (h) => {
    if (!h || typeof h !== "object" || Object.keys(h).length === 0) return false;
    return JSON.stringify(h) !== DEFAULT_SALON_HOURS_JSON;
};
// "Configurado" = o lojista salvou os horários explicitamente na tela de
// Configurações (flag salonHoursConfigured) OU a grade difere do padrão sugerido.
const isSalonHoursConfigured = (brand) =>
    brand?.salonHoursConfigured === true || hasCustomSalonHours(brand?.salonHours);

const SetupChecklist = ({ brand, clients, services, products, professionals, setActiveModule, updateBrand }) => {
    // Item próprio (não depende do Home pai) — GET /tenant/whatsapp/status.
    const { status: waStatus } = useWhatsAppStatus(10000);

    const items = [
        {
            id: "logo",
            done: Boolean(brand.logoUrl) && brand.logoUrl.trim().length > 0,
            label: "Suba o logo do seu salão",
            desc: "Aparece no app, recibos e link público",
            icon: "fa-image",
            target: "account",
            cta: "Subir logo",
        },
        {
            id: "professionals",
            done: (professionals || []).filter((p) => p.active !== false).length >= 1,
            label: "Cadastre seu primeiro colaborador",
            desc: "Sem pelo menos 1 pro, a Agenda não funciona",
            icon: "fa-user-tie",
            target: "employee",
            cta: "Cadastrar colaborador",
        },
        {
            id: "services",
            done: (services || []).length >= 1,
            label: "Adicione pelo menos 1 serviço",
            desc: "Define o que aparece no PDV e na Agenda",
            icon: "fa-scissors",
            target: "catalog",
            cta: "Adicionar serviço",
        },
        {
            id: "whatsapp",
            done: waStatus?.connected === true,
            label: "Conecte seu WhatsApp",
            desc: "Ative mensagens automáticas e o agente IA",
            icon: "fa-whatsapp",
            iconStyle: "brand",
            target: "whatsapp",
            cta: "Conectar WhatsApp",
        },
        {
            id: "clients",
            done: (clients || []).length >= 1,
            label: "Cadastre seu primeiro cliente",
            desc: "Comece a construir sua base 360º",
            icon: "fa-address-book",
            target: "clients",
            cta: "Adicionar cliente",
        },
        {
            id: "hours",
            done: isSalonHoursConfigured(brand),
            label: "Configure seus horários de atendimento",
            desc: "Defina quando seu salão está aberto para agendamentos",
            icon: "fa-clock",
            target: "account",
            cta: "Configurar horários",
        },
        {
            id: "marketing",
            done: (brand.welcomeMessage || "").length > 0,
            label: "Personalize a mensagem de boas-vindas",
            desc: "Texto enviado no WhatsApp ao novo cliente",
            icon: "fa-message",
            target: "marketing",
            cta: "Configurar mensagem",
        },
    ];

    const total = items.length;
    const done = items.filter((i) => i.done).length;
    const remaining = total - done;
    const progress = (done / total) * 100;
    const allComplete = remaining === 0;
    const [confetti, setConfetti] = useState(false);
    const celebratedRef = useRef(brand.setupChecklistCelebrated === true);

    const dismissed = brand.setupChecklistDismissed === true;
    // Stable reference to updateBrand so effect below can be tied to allComplete only.
    const updateBrandRef = useRef(updateBrand);
    useEffect(() => { updateBrandRef.current = updateBrand; }, [updateBrand]);

    // Fire celebration once when all items are complete and we haven't celebrated yet.
    // Confetti logic itself is untouched — only what happens afterwards changed: it no
    // longer auto-hides the whole thing, it settles into a persistent collapsed banner
    // (see render below) that stays until the user closes it manually.
    // Timers are intentionally NOT cleared on effect re-run — they must survive the
    // brand state updates that this very effect triggers.
    useEffect(() => {
        if (!allComplete || celebratedRef.current) return;
        celebratedRef.current = true;
        setConfetti(true);
        toast.success("🎉 Configuração concluída!", {
            description: "Parabéns! Seu salão está pronto para faturar mais. Boa estreia!",
            duration: 6000,
        });
        updateBrandRef.current({ setupChecklistCelebrated: true, setupCompletedAt: Date.now() });
        setTimeout(() => setConfetti(false), 3800);
    }, [allComplete]);

    // Hide only once the user explicitly closes the collapsed banner.
    if (dismissed) return null;

    return (
        <div
            data-testid="home-setup-checklist"
            className={`relative rounded-2xl border overflow-hidden transition-all ${
                allComplete
                    ? "border-emerald-500/30 bg-emerald-500/[0.04]"
                    : "border-amber-500/30 bg-amber-500/[0.04]"
            }`}
        >
            {confetti && <ConfettiBurst />}
            {allComplete ? (
                <div className="p-4 flex items-center justify-between gap-3" data-testid="setup-collapsed-banner">
                    <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 flex items-center justify-center text-sm shrink-0">
                            <i className="fa-solid fa-circle-check"></i>
                        </div>
                        <div className="text-sm font-semibold text-emerald-100">
                            ✅ Configuração concluída
                        </div>
                    </div>
                    <button
                        data-testid="setup-dismiss"
                        onClick={() => updateBrand({ setupChecklistDismissed: true })}
                        aria-label="Fechar"
                        className="h-8 w-8 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 flex items-center justify-center transition-colors shrink-0"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>
            ) : (
                <>
                    <div className="absolute inset-x-0 top-0 h-1 bg-amber-500/15">
                        <div
                            className="h-full bg-amber-400 transition-all duration-700"
                            style={{ width: `${progress}%`, boxShadow: "0 0 12px rgba(212,175,55,0.5)" }}
                        ></div>
                    </div>

                    <div className="p-4 md:p-5 lg:p-6">
                        <header className="flex items-center justify-between flex-wrap gap-3 mb-4">
                            <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-xl flex items-center justify-center text-lg bg-amber-500/15 text-amber-300 border border-amber-500/40">
                                    <i className="fa-solid fa-list-check"></i>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">
                                        Comece com o pé direito
                                    </div>
                                    <h3 className="salon-heading text-lg lg:text-xl text-white mt-0.5">
                                        Complete em 3 minutos: faltam {remaining} de {total} passos
                                    </h3>
                                </div>
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">
                                {done}/{total} concluídos
                            </div>
                        </header>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 md:gap-2.5 divide-y divide-white/5 md:divide-y-0">
                            {items.map((it) => (
                                <button
                                    key={it.id}
                                    data-testid={`setup-item-${it.id}`}
                                    data-done={it.done ? "true" : "false"}
                                    onClick={() => {
                                        // Per-item routing hint so the target module opens straight
                                        // on the exact action screen, not just its hub.
                                        if (it.id === "marketing") sessionStorage.setItem("kinng.route.marketing.tab", "welcome");
                                        if (it.id === "logo") sessionStorage.setItem("kinng.route.account.view", "profile");
                                        if (it.id === "hours") sessionStorage.setItem("kinng.route.account.view", "hours");
                                        if (it.id === "whatsapp") sessionStorage.setItem("kinng.route.whatsapp.view", "conexao");
                                        if (it.id === "clients") sessionStorage.setItem("kinng.route.clients.view", "new-client");
                                        // Deep-linked actions bounce back to Home when saved / exited.
                                        // (Every module below consumes the flag via returnHomeIfRequested.)
                                        if (["whatsapp", "clients", "hours", "marketing", "professionals", "services", "logo"].includes(it.id)) {
                                            setSetupReturnHome();
                                        }
                                        setActiveModule(it.target);
                                    }}
                                    className={`group text-left p-2.5 md:p-3 md:rounded-xl md:border transition-all ${
                                        it.done
                                            ? "md:border-emerald-500/25 md:bg-emerald-500/[0.04] hover:bg-emerald-500/[0.06] md:hover:bg-emerald-500/[0.08]"
                                            : "md:border-amber-500/25 md:bg-amber-500/[0.03] hover:bg-amber-500/[0.06] md:hover:bg-amber-500/[0.08] md:hover:border-amber-400"
                                    }`}
                                >
                                    <div className="flex items-start gap-2.5 mb-1.5 md:mb-2">
                                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 text-sm ${
                                            it.done
                                                ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                                                : "bg-amber-500/12 text-amber-300 border border-amber-500/30"
                                        }`}>
                                            <i className={`${it.done ? "fa-solid fa-check" : `${it.iconStyle === "brand" ? "fa-brands" : "fa-solid"} ${it.icon}`}`}></i>
                                        </div>
                                        {!it.done && (
                                            <span className="text-[9px] uppercase tracking-wider text-amber-400/80 font-bold mt-1.5">
                                                Pendente
                                            </span>
                                        )}
                                        {it.done && (
                                            <span className="text-[9px] uppercase tracking-wider text-emerald-300/80 font-bold mt-1.5">
                                                Concluído
                                            </span>
                                        )}
                                    </div>
                                    <div className={`text-xs font-bold leading-tight mb-0.5 text-white ${it.done ? "opacity-60" : ""}`}>
                                        {it.label}
                                    </div>
                                    <div className={`text-[9px] md:text-[10px] text-slate-400 leading-snug line-clamp-2 ${it.done ? "opacity-60" : ""}`}>{it.desc}</div>
                                    {!it.done && (
                                        <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-amber-300 font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                                            {it.cta}
                                            <i className="fa-solid fa-arrow-right"></i>
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};



// ─── Owner Journal · cronologia de marcos do dono ────────────────────────
const fmtDate = (ts) => {
    if (!ts) return null;
    const d = new Date(ts);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
};

export const OwnerJournal = ({ brand, clients, professionals, appointments }) => {
    const milestones = useMemo(() => {
        // Aggregate all transactions across pros
        const allTxns = [];
        professionals.forEach((p) => {
            (p.transactions || []).forEach((t) => allTxns.push({ ...t, proId: p.id, proName: p.name }));
        });
        allTxns.sort((a, b) => a.date - b.date);

        // Earliest pro (by createdAt fallback to position in array)
        const earliestPro = [...professionals]
            .filter((p) => p.active !== false)
            .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))[0];

        // Earliest client (by createdAt or first txn date if available)
        const earliestClient = [...(clients || [])]
            .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))[0];

        // Cumulative revenue progression to find threshold crossings
        let acc = 0;
        let firstThousand = null;
        let firstTenThousand = null;
        allTxns.forEach((t) => {
            acc += t.total || 0;
            if (!firstThousand && acc >= 1000) firstThousand = t.date;
            if (!firstTenThousand && acc >= 10000) firstTenThousand = t.date;
        });

        // 100th appointment milestone
        const completed = appointments.filter((a) => a.status === "concluido").sort((a, b) => a.date - b.date);
        const hundredthAppt = completed[99] ? completed[99].date : null;

        // 1-month anniversary
        const openedAt = brand.openedAt || brand.createdAt || null;
        const oneMonthDate = openedAt ? openedAt + 30 * 86400000 : null;
        const reachedOneMonth = oneMonthDate && Date.now() >= oneMonthDate;

        const list = [
            brand.setupChecklistCelebrated && {
                id: "setup-done",
                icon: "fa-trophy",
                accent: "amber",
                title: "Configuração inicial concluída",
                subtitle: "Salão pronto para faturar.",
                date: brand.setupCompletedAt || null,
            },
            earliestPro && {
                id: "first-pro",
                icon: "fa-medal",
                accent: "amber",
                title: `${earliestPro.name} foi seu primeiro colaborador`,
                subtitle: earliestPro.role || "Equipe formada",
                date: earliestPro.createdAt || null,
            },
            earliestClient && {
                id: "first-client",
                icon: "fa-handshake",
                accent: "emerald",
                title: `${earliestClient.name} foi seu primeiro cliente`,
                subtitle: "Sua base 360º começou aqui.",
                date: earliestClient.createdAt || null,
            },
            allTxns[0] && {
                id: "first-sale",
                icon: "fa-cash-register",
                accent: "emerald",
                title: "Primeira venda do salão",
                subtitle: `R$ ${(allTxns[0].total || 0).toFixed(2)} · ${allTxns[0].proName}`,
                date: allTxns[0].date,
            },
            firstThousand && {
                id: "first-1k",
                icon: "fa-sack-dollar",
                accent: "amber",
                title: "R$ 1.000 acumulados — primeiro mil",
                subtitle: "Sua máquina está girando.",
                date: firstThousand,
            },
            firstTenThousand && {
                id: "first-10k",
                icon: "fa-gem",
                accent: "violet",
                title: "R$ 10.000 acumulados",
                subtitle: "Crescimento de gente grande. Avalie o plano Imperial.",
                date: firstTenThousand,
            },
            hundredthAppt && {
                id: "hundredth-appt",
                icon: "fa-bolt",
                accent: "amber",
                title: "100º atendimento concluído",
                subtitle: "Sua agenda virou rotina.",
                date: hundredthAppt,
            },
            reachedOneMonth && {
                id: "anniv-1m",
                icon: "fa-cake-candles",
                accent: "rose",
                title: "1 mês de salão no Kinng",
                subtitle: "Continue assim — o segundo mês é o mais leve.",
                date: oneMonthDate,
            },
        ].filter(Boolean);

        // Sort by date desc; entries without date go to the end
        list.sort((a, b) => (b.date || 0) - (a.date || 0));
        return list;
    }, [brand, clients, professionals, appointments]);

    if (milestones.length === 0) return null;

    return (
        <section
            data-testid="home-owner-journal"
            className="rounded-2xl border border-white/8 bg-slate-900/40 backdrop-blur-xl p-5 lg:p-6"
        >
            <header className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-amber-500/12 border border-amber-500/30 text-amber-300 flex items-center justify-center">
                        <i className="fa-solid fa-book-open"></i>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 font-bold">
                            Diário do Dono
                        </div>
                        <h3 className="salon-heading text-lg lg:text-xl text-white mt-0.5">
                            Sua jornada até aqui
                        </h3>
                    </div>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                    {milestones.length} marco{milestones.length === 1 ? "" : "s"}
                </span>
            </header>

            <ol className="relative space-y-3" data-testid="owner-journal-list">
                <div className="absolute left-[19px] top-1 bottom-1 w-px bg-gradient-to-b from-amber-500/40 via-amber-500/20 to-transparent pointer-events-none"></div>
                {milestones.map((m) => (
                    <JournalEntry key={m.id} milestone={m} />
                ))}
            </ol>
        </section>
    );
};

const JOURNAL_ACCENTS = {
    amber: { ring: "border-amber-500/35", bg: "bg-amber-500/12 text-amber-300", title: "text-amber-100" },
    emerald: { ring: "border-emerald-500/35", bg: "bg-emerald-500/15 text-emerald-300", title: "text-emerald-100" },
    violet: { ring: "border-violet-500/35", bg: "bg-violet-500/15 text-violet-300", title: "text-violet-100" },
    rose: { ring: "border-rose-500/35", bg: "bg-rose-500/15 text-rose-300", title: "text-rose-100" },
};

const JournalEntry = ({ milestone }) => {
    const a = JOURNAL_ACCENTS[milestone.accent] || JOURNAL_ACCENTS.amber;
    const dateLabel = fmtDate(milestone.date);
    return (
        <li
            data-testid={`journal-entry-${milestone.id}`}
            className="relative pl-12 group"
        >
            <div className={`absolute left-0 top-1 h-10 w-10 rounded-xl border ${a.ring} ${a.bg} flex items-center justify-center z-10 transition-transform duration-300 group-hover:scale-[1.05]`}>
                <i className={`fa-solid ${milestone.icon}`}></i>
            </div>
            <div className="rounded-xl border border-white/5 bg-black/40 px-4 py-3 hover:border-white/15 transition-colors">
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <h4 className={`text-sm font-bold ${a.title} leading-snug`}>{milestone.title}</h4>
                    {dateLabel && (
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono shrink-0">
                            {dateLabel}
                        </span>
                    )}
                </div>
                {milestone.subtitle && (
                    <p className="text-[11px] text-slate-400 mt-1 leading-snug">{milestone.subtitle}</p>
                )}
            </div>
        </li>
    );
};

export const AlertCard = ({ alert, onClick, onWhatsApp }) => {
    const pal = ALERT_PALETTE[alert.kind] || ALERT_PALETTE.info;
    const hasWhatsApp = Boolean(alert.whatsappClient && alert.whatsappTemplateId);
    return (
        <div
            data-testid={`home-alert-${alert.id}`}
            className={`group relative rounded-2xl border ${pal.border} bg-black/40 p-4 overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-22px_rgba(212,175,55,0.4)]`}
        >
            <div className={`absolute inset-0 bg-gradient-to-br ${pal.bg} opacity-80 group-hover:opacity-100 transition-opacity pointer-events-none`}></div>
            <div className="relative flex items-start gap-3">
                <div className={`h-11 w-11 shrink-0 rounded-xl flex items-center justify-center border bg-gradient-to-br ${pal.iconBg} ${pal.icon} transition-transform duration-500 group-hover:scale-110`}>
                    <i
                        className={`${alert.iconStyle === "brand" ? "fa-brands" : "fa-solid"} ${alert.icon}`}
                        style={alert.iconColor ? { color: alert.iconColor } : undefined}
                    ></i>
                </div>
                <div className="min-w-0 flex-1">
                    <span className={`text-[9px] uppercase tracking-[0.2em] font-bold opacity-80 ${pal.cta}`}>
                        {pal.kindLabel}
                    </span>
                    <h4 className={`text-sm font-bold ${pal.title} leading-tight mt-1`}>
                        {alert.title}
                    </h4>
                    <p className={`text-[11px] ${pal.detail} mt-1 truncate`}>
                        {alert.detail}
                    </p>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <button
                            data-testid={`home-alert-${alert.id}-action`}
                            onClick={onClick}
                            className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1 px-2.5 py-1.5 rounded-lg border ${pal.border} hover:bg-white/5 ${pal.cta} transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50`}
                        >
                            {alert.actionLabel}
                            <i className="fa-solid fa-arrow-right text-[9px]"></i>
                        </button>
                        {hasWhatsApp && (
                            <button
                                data-testid={`home-alert-${alert.id}-whatsapp`}
                                onClick={onWhatsApp}
                                title={`Enviar WhatsApp para ${alert.whatsappClient.name.split(" ")[0]}`}
                                className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-500/45 bg-emerald-500/12 text-emerald-200 hover:bg-emerald-500/25 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
                            >
                                <i className="fa-brands fa-whatsapp text-sm"></i>
                                Enviar cupom
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── KPI Pill ─────────────────────────────────────────────────────────────
const KpiPill = ({ icon, label, value, suffix, highlight, accent }) => {
    const accentColor =
        accent === "emerald"
            ? { text: "#34d399", border: "border-emerald-500/30", ring: "ring-emerald-400/30" }
            : { text: "#fde7a8", border: "border-amber-500/20", ring: "ring-amber-400/30" };
    return (
        <div
            className={`rounded-xl border ${accentColor.border} bg-black/50 p-3 flex items-center gap-3 transition-all ${
                highlight ? `ring-1 ${accentColor.ring}` : ""
            }`}
        >
            <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-amber-500/12 border border-amber-500/25 text-amber-300 shrink-0">
                <i className={`fa-solid ${icon}`}></i>
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-amber-300/60 font-bold truncate">{label}</div>
                <div className="font-mono font-bold text-lg leading-tight truncate" style={{ color: accentColor.text }}>
                    {value}
                </div>
                <div className="text-[10px] text-amber-200/40 truncate">{suffix}</div>
            </div>
        </div>
    );
};

export default Home;
